/* Levi-Assisted Measurement Review + Contractor-Controlled Quantity
   Staging · Picker + Review Ceremony.

   Contractor-driven modal. Every action is triggered by an explicit
   click:
     - Choose a measurement  → step 1 (only approved records shown)
     - Choose a calc value   → step 2 (only values that exist)
     - Review and confirm    → step 3 (Use / Edit / Keep / Cancel)

   The picker never mutates an estimate line on open, on select, or on
   value pick. Only the final "Use Proposed Quantity" / "Use Edited"
   button issues the network call.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronLeft, X, Ruler } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../context/LangContext";

// Natural unit per calc field (mirrors the backend's _CALC_UNIT_MAP).
const CALC_UNIT = {
  floor_area: "sqft", ceiling_area: "sqft",
  gross_wall_area: "sqft", net_wall_area: "sqft",
  total_opening_area: "sqft", run_wall_area: "sqft",
  perimeter: "ft", linear_feet: "ft",
};

// Which calc fields are meaningful per measurement_mode. The picker
// still filters down to values that actually exist on the record.
const CALC_ORDER_BY_MODE = {
  rectangular:    ["floor_area", "ceiling_area", "perimeter", "gross_wall_area", "net_wall_area", "total_opening_area", "run_wall_area"],
  linear:         ["linear_feet", "perimeter"],
  custom_labeled: ["floor_area", "ceiling_area", "perimeter", "gross_wall_area", "net_wall_area", "linear_feet"],
};

const listCalcFields = (rec) => {
  const mode = rec?.measurement_mode || "rectangular";
  const order = CALC_ORDER_BY_MODE[mode] || CALC_ORDER_BY_MODE.rectangular;
  const calc = rec?.calculated || {};
  const out = [];
  for (const k of order) {
    const v = calc[k];
    if (v === null || v === undefined) continue;
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    out.push({ field: k, value: n, unit: CALC_UNIT[k] || "" });
  }
  return out;
};

const fmt = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "-";
  return Math.abs(x) >= 100 ? x.toFixed(0) : x.toFixed(2).replace(/\.00$/, "");
};

export const WorkAreaStagingPicker = ({
  projectId,
  estimateId,
  lineIndex,
  currentQuantity,
  currentUnit,
  currentContractorEdited,
  onStaged,
  onClose,
}) => {
  const { t } = useLang();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("choose_measurement"); // choose_measurement | choose_calc | review
  const [selectedRec, setSelectedRec] = useState(null);
  const [selectedCalc, setSelectedCalc] = useState(null); // {field, value, unit}
  const [editing, setEditing] = useState(false);
  const [editedQty, setEditedQty] = useState("");
  const [needsConfirmReplace, setNeedsConfirmReplace] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/projects/${projectId}/work-areas`);
        if (cancelled) return;
        const approved = (Array.isArray(data) ? data : [])
          .filter((r) => r && r.approval_status === "approved");
        setRecords(approved);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const chooseRecord = (rec) => {
    setSelectedRec(rec);
    setSelectedCalc(null);
    const fields = listCalcFields(rec);
    if (fields.length === 1) {
      setSelectedCalc(fields[0]);
      setStep("review");
    } else {
      setStep("choose_calc");
    }
  };

  const chooseCalc = (calc) => {
    setSelectedCalc(calc);
    setStep("review");
  };

  const backToChoose = () => {
    setSelectedCalc(null);
    setEditing(false);
    setEditedQty("");
    setNeedsConfirmReplace(false);
    setError(null);
    setStep("choose_measurement");
  };

  const proposedValue = selectedCalc?.value ?? null;
  const proposedUnit  = selectedCalc?.unit  ?? "";

  const existingQtyNum = useMemo(() => {
    const x = Number(currentQuantity);
    return Number.isFinite(x) ? x : 0;
  }, [currentQuantity]);
  const existingIsMeaningful = existingQtyNum > 0;
  const targetLineWasEdited = Boolean(currentContractorEdited);

  const doStage = useCallback(async ({ overrideQty, replaceExisting }) => {
    if (!selectedRec || !selectedCalc) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        work_area_id: selectedRec.id,
        calc_field: selectedCalc.field,
        replace_existing: !!replaceExisting,
        request_key: `wastage-${estimateId}-${lineIndex}-${selectedRec.id}-${selectedCalc.field}-${Date.now()}`,
      };
      if (overrideQty !== undefined && overrideQty !== null && !Number.isNaN(overrideQty)) {
        body.override_quantity = overrideQty;
      }
      const { data } = await api.post(
        `/estimates/${estimateId}/line-items/${lineIndex}/stage-from-work-area`,
        body,
      );
      onStaged?.(data);
      onClose?.();
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      if (code === "replacement_confirmation_required") {
        setNeedsConfirmReplace(true);
        setError(null);
      } else if (code) {
        const msg = t(`work_areas.errors.${code}`);
        setError(msg && !msg.startsWith("work_areas.errors.") ? msg : t("work_areas.errors.staging_failed"));
      } else {
        setError(t("work_areas.errors.staging_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  }, [selectedRec, selectedCalc, estimateId, lineIndex, onStaged, onClose, t]);

  const useProposed = () => doStage({ overrideQty: null, replaceExisting: needsConfirmReplace });
  const useEdited = () => {
    const n = Number(editedQty);
    if (!Number.isFinite(n) || n < 0) return;
    doStage({ overrideQty: n, replaceExisting: needsConfirmReplace });
  };
  const keepExisting = () => onClose?.();
  const cancelSkip  = () => onClose?.();

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-2 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="work-area-staging-picker"
    >
      <div className="jc-card w-full max-w-2xl bg-white shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--jc-border)]">
          <div className="flex items-center gap-2 min-w-0">
            {step !== "choose_measurement" && (
              <button
                type="button"
                onClick={backToChoose}
                className="p-1 text-[var(--jc-text-soft)] hover:text-[var(--jc-navy)]"
                data-testid="work-area-staging-back"
                aria-label={t("work_areas.staging.picker_title")}
              >
                <ChevronLeft size={16} />
              </button>
            )}
            <div className="min-w-0">
              <div className="font-display text-base" data-testid="work-area-staging-title">
                {t("work_areas.staging.picker_title")}
              </div>
              <div className="text-xs text-[var(--jc-text-soft)]">
                {t("work_areas.staging.picker_subtitle")}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={cancelSkip}
            className="p-1 text-[var(--jc-text-soft)] hover:text-[var(--jc-red)]"
            data-testid="work-area-staging-close"
            aria-label={t("work_areas.staging.cancel_skip")}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="text-sm text-[var(--jc-text-soft)]" data-testid="work-area-staging-loading">
              {t("work_areas.staging.staging_now")}
            </div>
          )}

          {!loading && records.length === 0 && (
            <div className="text-sm" data-testid="work-area-staging-empty">
              <div className="font-semibold text-[var(--jc-text)] mb-1">
                {t("work_areas.staging.no_approved")}
              </div>
              <div className="text-[var(--jc-text-soft)]">
                {t("work_areas.staging.no_approved_hint")}
              </div>
            </div>
          )}

          {!loading && records.length > 0 && step === "choose_measurement" && (
            <div data-testid="work-area-staging-choose-measurement">
              <div className="overline mb-2">{t("work_areas.staging.choose_measurement")}</div>
              <ul className="space-y-2">
                {records.map((rec) => {
                  const fields = listCalcFields(rec);
                  return (
                    <li key={rec.id}>
                      <button
                        type="button"
                        onClick={() => chooseRecord(rec)}
                        className="w-full text-left rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)] hover:bg-white p-3 flex items-start gap-3"
                        data-testid={`work-area-staging-record-${rec.id}`}
                      >
                        <div className="shrink-0 w-8 h-8 rounded-md bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] flex items-center justify-center">
                          <Ruler size={16} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-[var(--jc-text)] truncate">
                            {rec.name || t("work_areas.types.custom")}
                          </div>
                          <div className="text-xs text-[var(--jc-text-soft)]">
                            {t(`work_areas.types.${rec.area_type}`)} · {fields.length} {fields.length === 1 ? "value" : "values"}
                          </div>
                        </div>
                        <CheckCircle2 size={14} className="ml-auto text-emerald-600 shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {!loading && step === "choose_calc" && selectedRec && (
            <div data-testid="work-area-staging-choose-calc">
              <div className="overline mb-2">{t("work_areas.staging.choose_calc")}</div>
              <div className="text-sm mb-3">
                <b className="text-[var(--jc-text)]">{selectedRec.name}</b>
                <span className="text-[var(--jc-text-soft)]"> · {t(`work_areas.types.${selectedRec.area_type}`)}</span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {listCalcFields(selectedRec).map((c) => (
                  <li key={c.field}>
                    <button
                      type="button"
                      onClick={() => chooseCalc(c)}
                      className="w-full text-left rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)] hover:bg-white p-3"
                      data-testid={`work-area-staging-calc-${c.field}`}
                    >
                      <div className="text-xs text-[var(--jc-text-soft)]">
                        {t(`work_areas.calc.${c.field}`)}
                      </div>
                      <div className="text-sm font-semibold text-[var(--jc-text)] mt-0.5">
                        {fmt(c.value)} <span className="text-xs font-normal text-[var(--jc-text-soft)]">{t(`work_areas.units.${c.unit}`)}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!loading && step === "review" && selectedRec && selectedCalc && (
            <div data-testid="work-area-staging-review">
              <div className="overline mb-2">{t("work_areas.staging.review_title")}</div>
              <div className="rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)] p-3 text-sm">
                <div className="text-xs text-[var(--jc-text-soft)]">{t("work_areas.staging.review_source")}</div>
                <div className="font-semibold text-[var(--jc-text)]" data-testid="work-area-staging-review-source">
                  {selectedRec.name} · {t(`work_areas.calc.${selectedCalc.field}`)}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="rounded-md border border-[var(--jc-border)] bg-white p-3">
                  <div className="text-xs text-[var(--jc-text-soft)]">
                    {existingIsMeaningful ? t("work_areas.staging.current_quantity") : t("work_areas.staging.current_quantity_none")}
                  </div>
                  <div className="text-sm font-semibold text-[var(--jc-text)] mt-0.5" data-testid="work-area-staging-current-qty">
                    {existingIsMeaningful ? `${fmt(existingQtyNum)} ${currentUnit || ""}` : "\u2014"}
                  </div>
                </div>
                <div className="rounded-md border border-[var(--jc-border)] bg-white p-3">
                  <div className="text-xs text-[var(--jc-text-soft)]">{t("work_areas.staging.proposed_quantity")}</div>
                  {editing ? (
                    <div className="mt-0.5 flex items-center gap-2">
                      <input
                        type="number"
                        step="any"
                        value={editedQty}
                        onChange={(e) => setEditedQty(e.target.value)}
                        className="w-full text-right px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
                        data-testid="work-area-staging-edit-input"
                      />
                      <span className="text-xs text-[var(--jc-text-soft)]">{t(`work_areas.units.${selectedCalc.unit}`)}</span>
                    </div>
                  ) : (
                    <div className="text-sm font-semibold text-[var(--jc-text)] mt-0.5" data-testid="work-area-staging-proposed-qty">
                      {fmt(proposedValue)} <span className="text-xs font-normal text-[var(--jc-text-soft)]">{t(`work_areas.units.${selectedCalc.unit}`)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-2 text-xs text-[var(--jc-text-soft)]">
                {t("work_areas.staging.proposed_hint")}
              </div>

              {(existingIsMeaningful && (targetLineWasEdited || needsConfirmReplace)) && (
                <div
                  className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
                  data-testid="work-area-staging-replace-warning"
                >
                  {t("work_areas.staging.replace_warning")}
                </div>
              )}

              {error && (
                <div
                  className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-800"
                  data-testid="work-area-staging-error"
                >
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-2">
                {!editing && (
                  <button
                    type="button"
                    onClick={useProposed}
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
                    data-testid="work-area-staging-use-proposed-btn"
                  >
                    {t("work_areas.staging.use_proposed")}
                  </button>
                )}
                {!editing && (
                  <button
                    type="button"
                    onClick={() => { setEditing(true); setEditedQty(String(proposedValue ?? "")); }}
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border)] bg-white text-[var(--jc-text)] hover:bg-[var(--jc-bg)] disabled:opacity-60"
                    data-testid="work-area-staging-edit-btn"
                  >
                    {t("work_areas.staging.edit_before_using")}
                  </button>
                )}
                {editing && (
                  <>
                    <button
                      type="button"
                      onClick={useEdited}
                      disabled={submitting}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
                      data-testid="work-area-staging-use-edited-btn"
                    >
                      {t("work_areas.staging.use_proposed")}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditing(false); setEditedQty(""); }}
                      disabled={submitting}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border)] bg-white text-[var(--jc-text)] hover:bg-[var(--jc-bg)] disabled:opacity-60"
                      data-testid="work-area-staging-cancel-edit-btn"
                    >
                      {t("work_areas.staging.cancel_edit")}
                    </button>
                  </>
                )}
                {existingIsMeaningful && (
                  <button
                    type="button"
                    onClick={keepExisting}
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border)] bg-white text-[var(--jc-text)] hover:bg-[var(--jc-bg)] disabled:opacity-60"
                    data-testid="work-area-staging-keep-existing-btn"
                  >
                    {t("work_areas.staging.keep_existing")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={cancelSkip}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] disabled:opacity-60"
                  data-testid="work-area-staging-cancel-skip-btn"
                >
                  {t("work_areas.staging.cancel_skip")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkAreaStagingPicker;
